import { logger, generateRequestId } from '../utils/logger';

/**
 * Payment method service for handling multiple payment providers
 * Supports Stripe, PayPal, Google Pay, Apple Pay, and bank transfers
 */

const PAYMENT_METHODS = [
  {
    id: 'card',
    name: 'Credit/Debit Card',
    provider: 'stripe',
    icon: 'CreditCard',
    description: 'Visa, Mastercard, Amex',
    enabled: true,
    supportedCurrencies: ['GBP', 'USD', 'EUR', 'CAD', 'AUD', 'JPY', 'INR', 'CNY', 'BRL', 'ZAR']
  },
  {
    id: 'paypal',
    name: 'PayPal',
    provider: 'paypal',
    icon: 'Wallet',
    description: 'Pay with your PayPal account',
    enabled: true,
    supportedCurrencies: ['GBP', 'USD', 'EUR', 'CAD', 'AUD', 'JPY', 'INR', 'CNY', 'BRL']
  },
  {
    id: 'google_pay',
    name: 'Google Pay',
    provider: 'google_pay',
    icon: 'Smartphone',
    description: 'Fast checkout with Google Pay',
    enabled: true,
    supportedCurrencies: ['GBP', 'USD', 'EUR', 'CAD', 'AUD', 'JPY', 'INR']
  },
  {
    id: 'apple_pay',
    name: 'Apple Pay',
    provider: 'apple_pay',
    icon: 'Apple',
    description: 'Secure payment with Apple Pay',
    enabled: true,
    supportedCurrencies: ['GBP', 'USD', 'EUR', 'CAD', 'AUD', 'JPY']
  },
  {
    id: 'bank_transfer',
    name: 'Bank Transfer',
    provider: 'bank_transfer',
    icon: 'Building2',
    description: 'Direct bank transfer (SEPA, ACH)',
    enabled: true,
    supportedCurrencies: ['GBP', 'USD', 'EUR']
  }
];

export const paymentMethodService = {
  /**
   * Get all available payment methods
   * @param {string} currency - User's currency to filter supported methods
   * @returns {Array}
   */
  getAvailablePaymentMethods(currency = 'GBP') {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    const availableMethods = PAYMENT_METHODS?.filter(method => 
      method?.enabled && method?.supportedCurrencies?.includes(currency)
    );

    logger?.debug('Available payment methods retrieved', { 
      requestId, 
      currency, 
      methodsCount: availableMethods?.length 
    });

    return availableMethods;
  },

  /**
   * Check if Apple Pay is available on device
   * @returns {boolean}
   */
  isApplePayAvailable() {
    if (typeof window !== 'undefined' && window?.ApplePaySession) {
      return window.ApplePaySession?.canMakePayments();
    }
    return false;
  },

  /**
   * Check if Google Pay is available on device
   * @returns {Promise<boolean>}
   */
  async isGooglePayAvailable() {
    try {
      if (typeof window !== 'undefined' && window?.google?.payments?.api?.PaymentsClient) {
        const paymentsClient = new window.google.payments.api.PaymentsClient({
          environment: 'PRODUCTION'
        });
        
        const isReadyToPayRequest = {
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [{
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['MASTERCARD', 'VISA']
            }
          }]
        };
        
        const response = await paymentsClient?.isReadyToPay(isReadyToPayRequest);
        return response?.result || false;
      }
      return false;
    } catch (error) {
      logger?.error('Google Pay availability check failed', error);
      return false;
    }
  },

  /**
   * Get payment method by ID
   * @param {string} methodId - Payment method ID
   * @returns {Object|null}
   */
  getPaymentMethod(methodId) {
    return PAYMENT_METHODS?.find(method => method?.id === methodId) || null;
  },

  /**
   * Validate payment method for currency
   * @param {string} methodId - Payment method ID
   * @param {string} currency - Currency code
   * @returns {boolean}
   */
  isMethodSupportedForCurrency(methodId, currency) {
    const method = this.getPaymentMethod(methodId);
    return method?.supportedCurrencies?.includes(currency) || false;
  },

  /**
   * Get user's preferred payment method from storage
   * @returns {string|null}
   */
  getPreferredPaymentMethod() {
    return localStorage?.getItem('preferred_payment_method') || null;
  },

  /**
   * Set user's preferred payment method
   * @param {string} methodId - Payment method ID
   */
  setPreferredPaymentMethod(methodId) {
    localStorage?.setItem('preferred_payment_method', methodId);
    logger?.debug('Preferred payment method set', { methodId });
  },

  /**
   * Get payment method icon name for rendering
   * @param {string} methodId - Payment method ID
   * @returns {string}
   */
  getPaymentMethodIcon(methodId) {
    const method = this.getPaymentMethod(methodId);
    return method?.icon || 'CreditCard';
  }
};

export default paymentMethodService;